import { z } from 'zod';

import { type EnhancedToolResult, type ToolExecutionContext } from '../types';

import { AddAIVoiceoverSceneGroupTool } from './AddAIVoiceoverSceneGroupTool';
import { Tool } from './Tool';
import { formatVoiceTraits } from './voice-trait-format';
import { AI_VOICEOVER_DISABLED_MESSAGE } from './constants';

import {
    type AIVoice,
    type AIVoiceFilter,
    type AiVoiceV2ListData,
    ApiService,
} from 'js/src/client';
import {
    getCanUseAIVoiceover,
    getCanUsePremiumAIVoices,
} from 'js/src/data/account/account-selectors';

export const searchVoicesSchema = z.object({
    gender: z.string().optional().describe('Voice gender.'),
    accent: z.string().optional().describe('Voice accent.'),
    tone: z.string().optional().describe('Voice tone.'),
    style: z.string().optional().describe('Use-case/style of the voice.'),
    age: z.string().optional().describe('Voice age.'),
    language: z.string().optional().describe('Spoken language.'),
    search: z
        .string()
        .optional()
        .describe('Optional free-text keyword over voice name/attributes.'),
    count: z.number().optional().describe('Number of candidates to return (default 5).'),
});
export type SearchVoicesArgs = z.infer<typeof searchVoicesSchema>;

const DEFAULT_COUNT = 5;
const MIN_COUNT = 1;
const MAX_COUNT = 10;
const MAX_PAGE_SIZE = 100;
const HIGH_QUALITY = 'high_quality';
const MAX_VALUES_SHOWN = 25;

type VoiceQuery = NonNullable<AiVoiceV2ListData['query']>;

// The structured filter types the agent can pass. Each maps to a fixed,
// normalized vocabulary the LLM cannot know ahead of time — so we resolve the
// agent's free-text value against it rather than passing it through blindly.
const RESOLVABLE_FILTER_TYPES = ['gender', 'accent', 'tone', 'style', 'age', 'language'] as const;
type ResolvableFilterType = (typeof RESOLVABLE_FILTER_TYPES)[number];

// Short, decision-driving types whose valid values are worth enumerating in the
// result. The long types (accent, language) are only named as available unless
// the agent passed an unrecognized one, in which case their values are listed so
// it can correct.
const ENUMERATED_TYPES: ResolvableFilterType[] = ['gender', 'tone', 'style', 'age'];

interface FilterOption {
    value: string;
    display: string;
}
type Vocabulary = Map<string, FilterOption[]>;
type ResolvedFilters = Partial<Record<ResolvableFilterType, string>>;
interface IgnoredFilter {
    type: ResolvableFilterType;
    given: string;
}

// Module-level cache of the voice-filter vocabulary (same source the Creator's
// VoiceSelectionModal dropdowns use). Fetched lazily on first search and reused
// for the rest of the session.
let vocabularyCache: Vocabulary | null = null;
let vocabularyInFlight: Promise<Vocabulary> | null = null;

async function getFilterVocabulary(): Promise<Vocabulary> {
    if (vocabularyCache) return vocabularyCache;
    if (!vocabularyInFlight) {
        vocabularyInFlight = (async () => {
            const response = await ApiService.aiVoiceFilterList({
                query: {
                    gender: true,
                    accent: true,
                    tone: true,
                    style: true,
                    age: true,
                    language: true,
                },
            });
            const map: Vocabulary = new Map();
            for (const f of (response.data ?? []) as AIVoiceFilter[]) {
                const list = map.get(f.type) ?? [];
                list.push({ value: f.value, display: f.display });
                map.set(f.type, list);
            }
            vocabularyCache = map;
            return map;
        })();
    }
    try {
        return await vocabularyInFlight;
    } finally {
        // Clear the in-flight handle so a failed fetch is retried next time
        // (a successful one is served from vocabularyCache via the early return).
        vocabularyInFlight = null;
    }
}

// Test seam: reset the module cache between cases.
export function clearFilterVocabularyCache(): void {
    vocabularyCache = null;
    vocabularyInFlight = null;
}

function normalizeToken(value: string): string {
    return value
        .toLowerCase()
        .trim()
        .replace(/[\s_]+/g, ' ');
}

function resolveFilterValue(given: string, options: FilterOption[]): string | undefined {
    const normalized = normalizeToken(given);
    const hit = options.find(
        (o) => normalizeToken(o.value) === normalized || normalizeToken(o.display) === normalized,
    );
    return hit?.value;
}

// Resolve each provided filter against the vocabulary. Recognized values map to
// their canonical form and are applied; unrecognized ones are dropped (so a
// single bad value can't zero the whole result) and reported back.
function resolveProvidedFilters(
    args: SearchVoicesArgs,
    vocab: Vocabulary,
): { resolved: ResolvedFilters; ignored: IgnoredFilter[] } {
    const resolved: ResolvedFilters = {};
    const ignored: IgnoredFilter[] = [];
    for (const type of RESOLVABLE_FILTER_TYPES) {
        const given = args[type];
        if (!given) continue;
        const options = vocab.get(type) ?? [];
        const canonical = resolveFilterValue(given, options);
        if (canonical) {
            resolved[type] = canonical;
        } else {
            ignored.push({ type, given });
        }
    }
    return { resolved, ignored };
}

// Fallback when the vocabulary endpoint is unavailable: pass the provided values
// through unresolved rather than failing the search outright.
function rawFilters(args: SearchVoicesArgs): ResolvedFilters {
    const resolved: ResolvedFilters = {};
    for (const type of RESOLVABLE_FILTER_TYPES) {
        const given = args[type];
        if (given) resolved[type] = given;
    }
    return resolved;
}

function describeIgnored(ignored: IgnoredFilter[]): string {
    if (ignored.length === 0) return '';
    const parts = ignored.map(({ type, given }) => `${type}="${given}"`);
    return (
        `Ignored ${parts.length} unrecognized filter value${parts.length === 1 ? '' : 's'}: ` +
        `${parts.join('; ')} — see the valid values below.\n`
    );
}

// Teaches the agent the one thing the tool schema can't: the valid *values* for
// each filter (the schema already lists which params exist). Only surfaces
// values that are still actionable — a type the agent used with a valid value is
// omitted (no point repeating "male, female" when it asked for male). Long types
// (accent/language) are only listed once the agent gets one wrong, so the lean
// case stays lean.
function describeFilterOptions(
    resolved: ResolvedFilters,
    ignored: IgnoredFilter[],
    vocab: Vocabulary,
    // On a zero-result reply nothing is omitted: the agent may need to CHANGE
    // a valid-but-wrong value (swap gender, drop tone), so repeating the
    // vocabulary of types it already used is exactly the actionable part.
    includeResolved = false,
): string {
    const resolvedTypes = new Set<string>(Object.keys(resolved));
    const ignoredTypes = new Set<string>(ignored.map((i) => i.type));
    const valueSegments: string[] = [];

    for (const type of RESOLVABLE_FILTER_TYPES) {
        // Already used a valid value — don't repeat it (unless zero results).
        if (!includeResolved && resolvedTypes.has(type)) continue;
        // Enumerate short types (values the agent can't guess) and any long type
        // it got wrong; skip unused long types — the schema already names them.
        if (!ENUMERATED_TYPES.includes(type) && !ignoredTypes.has(type)) continue;
        const options = vocab.get(type);
        if (!options?.length) continue;
        const values = options.map((o) => o.value).slice(0, MAX_VALUES_SHOWN);
        valueSegments.push(`${type}: ${values.join(', ')}`);
    }

    if (valueSegments.length === 0) return '';
    return `\n\nFilter values you can use — ${valueSegments.join('; ')}.`;
}

/**
 * Searches the v2 voice API for AI voiceover candidates. Agent-specific bits —
 * how the agent should commit the chosen slug, and any bookkeeping per
 * candidate — are protected hooks so each root agent can adapt them (see the
 * codeo subclass).
 */
export class SearchVoicesTool extends Tool<typeof searchVoicesSchema> {
    public static readonly toolName = 'search_voices';

    protected override readonly schema = searchVoicesSchema;
    protected override readonly toolName = SearchVoicesTool.toolName;
    // The commit-guidance tail is a virtual method a subclass overrides (codeo points at a
    // different commit path), so the description is computed per-instance rather than a static
    // string. A field initializer runs at construction and dispatches to the subclass override.
    protected override readonly toolDescription =
        'Find AI voiceover voice candidates for the video. Returns a ranked ' +
        'list of higher-quality voices matching the structured filters you ' +
        'pass; does not change the composition. A hardcoded quality gate means ' +
        'every result is already a good voice — you do not filter for quality. ' +
        'Filters use a fixed vocabulary you cannot guess: the result lists the ' +
        'valid values, and any unrecognized value is ignored (not an error) and ' +
        'reported — so a bad value never zeroes the search. Search broadly first, ' +
        'then refine with exact values from the result. ' +
        this.getCommitGuidanceForDescription();

    public getToolDisplayName(): string {
        return 'Searching voices';
    }

    // How the agent should commit the chosen slug — appended to the tool
    // description (schema) and to every result.
    protected getCommitGuidanceForDescription(): string {
        return `Pass the best slug as voice_slug to ${AddAIVoiceoverSceneGroupTool.toolName}.`;
    }

    protected getCommitGuidanceForResult(): string {
        return `Pass the best fit as voice_slug to ${AddAIVoiceoverSceneGroupTool.toolName}.`;
    }

    // Called once per returned candidate, before the result is formatted.
    protected registerCandidate(_voice: AIVoice): void {}

    public get statusIcon(): string {
        return 'graphic_eq';
    }

    public async execute(
        args: SearchVoicesArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        if (!getCanUseAIVoiceover(this.getState())) {
            return { result: AI_VOICEOVER_DISABLED_MESSAGE, isSuccess: false };
        }

        // A team without premium access only ever has a couple of usable voices,
        // so the vocab-resolution / quality-gate / relaxed-retry machinery below
        // is wasted work — just list what's available and point at an upgrade.
        if (!this.canUsePremiumVoices()) {
            return this.executeWithoutPremiumVoices();
        }

        const requested = args.count ?? DEFAULT_COUNT;
        const count = Math.max(MIN_COUNT, Math.min(MAX_COUNT, Math.floor(requested)));
        // Fetch the full page, not just count, so capCustomShare has stock voices
        // to surface: the team's custom voices sort first, so a small page can be
        // entirely custom and leave nothing to balance with.
        const pageSize = MAX_PAGE_SIZE;

        try {
            const vocab = await getFilterVocabulary().catch(() => null);
            const { resolved, ignored } = vocab
                ? resolveProvidedFilters(args, vocab)
                : { resolved: rawFilters(args), ignored: [] as IgnoredFilter[] };
            const ignoredNote = describeIgnored(ignored);
            const optionsBlock = vocab ? describeFilterOptions(resolved, ignored, vocab) : '';

            // Hard quality gate: high_quality voices only.
            let voices = await this.fetchVoices(resolved, args.search, pageSize, true);
            let relaxed = false;
            // Fallback: a team without any usable high-quality voices would
            // otherwise get nothing — retry without the quality filter.
            if (voices.length === 0) {
                voices = await this.fetchVoices(resolved, args.search, pageSize, false);
                relaxed = voices.length > 0;
            }

            if (voices.length === 0) {
                // The full vocabulary, including types already used validly —
                // on zero results the agent may need to change any of them.
                const allOptions = vocab
                    ? describeFilterOptions(resolved, ignored, vocab, true)
                    : '';
                // Rather than a dead-end "no match", fall back to the top
                // trending usable voices (on a plan-gated team that can be the
                // whole usable pool) so the agent escapes in one round —
                // pick a suggestion, or refine with the listed values.
                const hadFilters = Object.keys(resolved).length > 0 || !!args.search;
                const fallback = hadFilters ? await this.fetchUnfiltered(pageSize) : [];
                if (fallback.length > 0) {
                    const suggestions = capCustomShare(fallback, count);
                    for (const v of suggestions) this.registerCandidate(v);
                    return {
                        result:
                            ignoredNote +
                            'No voices matched those filters. The closest usable voices by ' +
                            'popularity — pick one of these, or search again with values from below:\n' +
                            formatCandidateLines(suggestions).join('\n') +
                            '\n\n' +
                            this.getCommitGuidanceForResult() +
                            allOptions,
                        isSuccess: true,
                    };
                }
                return {
                    result:
                        ignoredNote +
                        'No voices matched those filters. Try removing one (e.g. drop ' +
                        'accent or age) and search again.' +
                        allOptions,
                    isSuccess: true,
                };
            }

            const candidates = capCustomShare(voices, count);
            for (const v of candidates) this.registerCandidate(v);

            const lines = formatCandidateLines(candidates);

            const relaxedNote = relaxed
                ? 'No high-quality voices were available for this team, so the quality ' +
                  'filter was relaxed. '
                : '';

            return {
                result:
                    ignoredNote +
                    `${relaxedNote}Found ${candidates.length} voice candidate${candidates.length === 1 ? '' : 's'}:\n` +
                    lines.join('\n') +
                    '\n\n' +
                    this.getCommitGuidanceForResult() +
                    optionsBlock,
                isSuccess: true,
            };
        } catch (e) {
            return {
                result: `${SearchVoicesTool.toolName} failed: ${describeError(e)}`,
                isSuccess: false,
            };
        }
    }

    // Teams without premium access have only a couple of usable voices, so
    // there's nothing to filter or rank: fetch the whole catalog once, list what
    // they can use, and point the agent at upgrading for the rest.
    //
    // Phrased by entitlement rather than plan name on purpose: premium_ai_voices
    // is off on several paid plans (MAXA, StackCommerce, AppSumo) and is
    // admin-editable per team, so "you're on the free plan" would be wrong for
    // paying customers.
    private async executeWithoutPremiumVoices(): Promise<EnhancedToolResult> {
        try {
            // tasting-menu, not the paginated list: it is unpaginated and always
            // includes every free public voice plus the team's brand voices. The
            // list endpoint caps at 100 and counts premium voices (which it
            // deliberately includes to upsell) against that cap, so a free voice
            // ranked below the cap would never survive the filter below.
            const response = await ApiService.aiVoiceV2TastingMenuList();
            const results = response.data ?? [];
            // The tasting menu also carries a capped premium slice, so paid voices
            // still have to be dropped here.
            const voices = results.filter((v) => !v.is_deprecated && !v.is_paid);
            for (const v of voices) this.registerCandidate(v);
            const lines = formatCandidateLines(voices);

            return {
                result:
                    "These are the voices included in this team's plan:\n" +
                    (lines.length > 0 ? lines.join('\n') : '(none available)') +
                    '\n\nFor access to thousands of premium AI voices, suggest the user to upgrade their plan.\n\n' +
                    this.getCommitGuidanceForResult(),
                isSuccess: true,
            };
        } catch (e) {
            return {
                result: `${SearchVoicesTool.toolName} failed: ${describeError(e)}`,
                isSuccess: false,
            };
        }
    }

    // Top trending usable voices with no structured filters — same
    // quality-then-relaxed ladder as the main search.
    private async fetchUnfiltered(pageSize: number): Promise<AIVoice[]> {
        const pool = await this.fetchVoices({}, undefined, pageSize, true);
        if (pool.length > 0) return pool;
        return this.fetchVoices({}, undefined, pageSize, false);
    }

    // Decides which path execute() takes: teams without this entitlement skip the
    // filtered search entirely and get the plan's voices listed instead. Fails
    // open when account state is unavailable — the server still enforces access
    // at generation time.
    private canUsePremiumVoices(): boolean {
        try {
            return getCanUsePremiumAIVoices(this.getState());
        } catch {
            return true;
        }
    }

    private async fetchVoices(
        filters: ResolvedFilters,
        search: string | undefined,
        pageSize: number,
        highQualityOnly: boolean,
    ): Promise<AIVoice[]> {
        const query: VoiceQuery = { sort: 'trending', page_size: pageSize };
        if (highQualityOnly) query.quality = HIGH_QUALITY;
        if (filters.gender) query.gender = filters.gender;
        if (filters.accent) query.accent = filters.accent;
        if (filters.tone) query.tone = filters.tone;
        if (filters.style) query.style = filters.style;
        if (filters.age) query.age = filters.age;
        if (filters.language) query.language = [filters.language];
        if (search) query.search = search;

        const response = await ApiService.aiVoiceV2List({ query });
        const results = response.data?.results ?? [];
        // Deprecated voices are still returned by the API (only deprioritized in
        // sort) — exclude them so the agent never considers a discouraged voice.
        // Paid voices need no filtering here: execute() only reaches this path
        // when the team can use premium voices.
        return results.filter((v) => !v.is_deprecated);
    }
}

/**
 * Pick `count` voices from a trending-ordered pool while capping the team's own
 * custom (private) voices at half the list. The backend sort leads with
 * `-is_team_voice`, so a team's custom voices sort ahead of every stock voice —
 * taken naively the top N is all custom and the user never sees stock ElevenLabs
 * options. We cap custom voices at ceil(count/2), fill the rest with the stock
 * voices ranked below them, and backfill from the pool only if there aren't
 * enough stock voices to reach `count` (so we never return fewer than before).
 * Selection preserves the pool's trending order — only excess customs are dropped.
 */
export function capCustomShare(pool: AIVoice[], count: number): AIVoice[] {
    const maxCustom = Math.ceil(count / 2);
    const selected = new Set<AIVoice>();
    let customTaken = 0;
    for (const v of pool) {
        if (selected.size >= count) break;
        if (v.is_private) {
            if (customTaken >= maxCustom) continue;
            customTaken += 1;
        }
        selected.add(v);
    }
    // Not enough stock to fill the list — backfill with the customs skipped above.
    for (const v of pool) {
        if (selected.size >= count) break;
        selected.add(v);
    }
    return pool.filter((v) => selected.has(v));
}

// Languages are deliberately uncapped here: the search result is the agent's
// only complete view of what a voice can speak, so a truncated list would make
// it rule out voices that do support the user's language.
function formatCandidateLines(voices: AIVoice[]): string[] {
    return voices.map((v, i) => {
        const traits = formatVoiceTraits(v);
        const paid = v.is_paid ? ' (premium)' : '';
        const traitsText = traits ? `  ${traits}` : '';
        return `[${i + 1}] slug=${v.slug}  "${v.display_name}"${traitsText}${paid}`;
    });
}

// ApiService rejects with either a bare Error or an ErrorObject ({ error: Error }).
// Stringifying the latter directly yields "[object Object]", so peel it.
function describeError(e: unknown): string {
    if (e instanceof Error) return e.message;
    if (e && typeof e === 'object' && 'error' in e) {
        const inner = (e as { error: unknown }).error;
        if (inner instanceof Error) return inner.message;
        if (typeof inner === 'string') return inner;
    }
    return String(e);
}
